import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';

export default function CustomerPortalPage({ initialTab = 'list' }: { initialTab?: 'list' | 'new' }) {
  const [tab, setTab] = useState<'list' | 'new'>(initialTab);
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ['portal-tickets'], queryFn: async () => (await api.get('/portal/tickets')).data });

  return (
    <div className="p-8 max-w-3xl">
      <h1 className="text-2xl font-semibold mb-1">Support</h1>
      <p className="text-sm text-slate-500 mb-6">View and raise tickets</p>

      <div className="flex gap-2 mb-4">
        <button className={'btn-ghost ' + (tab === 'list' ? 'bg-slate-100' : '')} onClick={() => setTab('list')}>My tickets</button>
        <button className={'btn-ghost ' + (tab === 'new' ? 'bg-slate-100' : '')} onClick={() => setTab('new')}>New ticket</button>
      </div>

      {tab === 'list' ? (
        <div className="card overflow-hidden">
          <ul className="divide-y divide-slate-100">
            {(data as { id: string; ticketNo: string; subject: string; status: string; updatedAt: string; assignee?: { name: string } | null }[]).map((t) => (
              <li key={t.id}><Link to={`/portal/tickets/${t.id}`} className="block px-4 py-3 hover:bg-slate-50">
                <div className="flex justify-between">
                  <div><span className="font-mono text-xs text-slate-500 mr-2">{t.ticketNo}</span><span className="font-medium">{t.subject}</span></div>
                  <div className="text-xs text-slate-500"><span className="badge bg-slate-100 mr-2">{t.status}</span>{new Date(t.updatedAt).toLocaleDateString()}</div>
                </div>
                <div className="text-xs text-slate-500 mt-1">Assigned: {t.assignee?.name ?? 'unassigned'}</div>
              </Link></li>
            ))}
            {(data as unknown[]).length === 0 && <li className="px-4 py-8 text-center text-slate-500">No tickets yet.</li>}
          </ul>
        </div>
      ) : (
        <NewTicketForm onCreated={() => { qc.invalidateQueries({ queryKey: ['portal-tickets'] }); setTab('list'); }} />
      )}
    </div>
  );
}

function NewTicketForm({ onCreated }: { onCreated: () => void }) {
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('NORMAL');
  const submit = useMutation({
    mutationFn: async () => api.post('/portal/tickets', { subject, description, priority }),
    onSuccess: () => { toast.success('Ticket raised'); onCreated(); },
    onError: (e: { response?: { data?: { error?: string } } }) => toast.error(e.response?.data?.error ?? 'Failed'),
  });
  return (
    <div className="card p-6">
      <div className="space-y-3">
        <div><label className="label">Subject</label><input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} /></div>
        <div><label className="label">Describe the issue</label><textarea className="input min-h-[140px]" value={description} onChange={(e) => setDescription(e.target.value)} /></div>
        <div><label className="label">Priority</label><select className="input max-w-[200px]" value={priority} onChange={(e) => setPriority(e.target.value)}>
          {['LOW', 'NORMAL', 'HIGH', 'CRITICAL'].map((p) => <option key={p}>{p}</option>)}
        </select></div>
      </div>
      <div className="mt-5">
        <button className="btn-primary" disabled={!subject || submit.isPending} onClick={() => submit.mutate()}>{submit.isPending ? 'Submitting…' : 'Submit ticket'}</button>
      </div>
    </div>
  );
}
